import { useEffect, useRef } from 'react';
import { motion, useAnimate, useReducedMotion } from 'motion/react';
import { Avatar } from './Avatar';
import { Burst } from './Burst';
import { Emoji } from './Emoji';
import { cn, upper, type Team } from './util';

export interface VsFighter {
  name: string;
  avatar: string;
  teamName: string;
  team: Team;
}

export interface VsSplashProps {
  left: VsFighter;
  right: VsFighter;
  category?: { name: string; emoji: string };
  round?: { current: number; total: number };
  /** Fires when the VS burst lands (play a sound / shake here). */
  onImpact?: () => void;
  /** Fires when the entrance sequence is complete (~1.4 s). */
  onDone?: () => void;
  className?: string;
}

function FighterSide({
  f,
  side,
  reduce,
}: {
  f: VsFighter;
  side: 'left' | 'right';
  reduce: boolean;
}) {
  const dir = side === 'left' ? -1 : 1;
  return (
    <motion.div
      className={cn(
        'relative z-10 flex min-w-0 flex-col items-center gap-3 text-center',
        side === 'left' ? 'sm:pr-[12%]' : 'sm:pl-[12%]',
      )}
      initial={reduce ? { opacity: 0 } : { x: `${dir * 120}%`, rotate: dir * 18, opacity: 0 }}
      animate={{ x: 0, rotate: dir * -2, opacity: 1 }}
      transition={{
        type: 'spring',
        stiffness: 380,
        damping: 19,
        delay: side === 'left' ? 0.05 : 0.2,
      }}
    >
      <motion.div
        animate={reduce ? undefined : { y: [0, -6, 0] }}
        transition={{
          duration: 1.4,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1.2 + (side === 'left' ? 0 : 0.35),
        }}
      >
        <Avatar emoji={f.avatar} name={f.name} team={f.team} size="hero" />
      </motion.div>
      <div className="comic ink-text-lg max-w-full text-[26px] leading-[0.9] [overflow-wrap:anywhere] text-white sm:text-[clamp(40px,5.2vw,76px)]">
        {upper(f.name)}
      </div>
      <div
        className="comic inline-flex max-w-full items-center gap-2 border-3 border-ink bg-white px-2 pt-1.5 pb-1 text-[15px] shadow-ink-sm sm:px-3 sm:text-[clamp(16px,1.6vw,22px)]"
        style={{ rotate: `${dir * -3}deg` }}
      >
        <span
          className="flex size-6 shrink-0 items-center justify-center border-2 border-ink text-[15px] text-white"
          style={{
            backgroundColor: f.team === 'A' ? 'var(--color-team-a)' : 'var(--color-team-b)',
          }}
        >
          {f.team}
        </span>
        {upper(f.teamName)}
      </div>
    </motion.div>
  );
}

/** The POW "VS" burst; its size comes from the `--vs-burst` variable set on the fighters' grid. */
function VsBurst({ reduce }: { reduce: boolean }) {
  return (
    <motion.div
      className="z-20 col-start-2 row-start-1 sm:absolute sm:col-start-auto sm:row-start-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-[58%]"
      initial={reduce ? { opacity: 0 } : { scale: 0, rotate: -40 }}
      animate={{ scale: 1, rotate: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 520, damping: 13, delay: 0.42 }}
    >
      <Burst
        size="var(--vs-burst)"
        fill="#ffde00"
        innerFill="#fff6b0"
        spikes={13}
        seed={9}
        strokeWidth={7}
        pulse={!reduce}
      >
        <span className="comic ink-text-xl block -rotate-6 pt-1 text-[36px] text-team-b sm:pt-2 sm:text-[clamp(56px,6vw,84px)]">
          VS
        </span>
      </Burst>
    </motion.div>
  );
}

/**
 * Round intro "VS" splash: both buyers slam in from the sides over their team colours, a POW burst
 * stamps "VS" in the middle, then the category banner rises. Re-mount (new `key`) to replay.
 */
export function VsSplash({
  left,
  right,
  category,
  round,
  onImpact,
  onDone,
  className,
}: VsSplashProps) {
  const reduce = !!useReducedMotion();
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const cbs = useRef({ onImpact, onDone });
  useEffect(() => {
    cbs.current = { onImpact, onDone };
  });

  useEffect(() => {
    const impact = setTimeout(() => {
      cbs.current.onImpact?.();
      if (!reduce && scope.current) {
        animate(
          scope.current,
          { x: [0, -10, 9, -6, 4, 0], y: [0, 5, -4, 3, 0, 0] },
          { duration: 0.38 },
        );
      }
    }, 520);
    const done = setTimeout(() => cbs.current.onDone?.(), 1450);
    return () => {
      clearTimeout(impact);
      clearTimeout(done);
    };
  }, [animate, scope, reduce]);

  return (
    <div
      ref={scope}
      role="img"
      aria-label={`${left.name} (${left.teamName}) ile ${right.name} (${right.teamName}) karşı karşıya${category ? `: ${category.name}` : ''}`}
      className={cn(
        'relative isolate flex min-h-[420px] w-full items-center overflow-hidden border-4 border-ink bg-ink shadow-ink-lg',
        className,
      )}
    >
      {/* team halves with a diagonal ink split */}
      <motion.div
        aria-hidden
        className="halftone-light absolute inset-0 bg-team-a"
        style={{ clipPath: 'polygon(0 0, 56% 0, 46% 100%, 0 100%)' }}
        initial={reduce ? false : { x: '-60%' }}
        animate={{ x: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 30 }}
      />
      <motion.div
        aria-hidden
        className="halftone-light absolute inset-0 bg-team-b"
        style={{ clipPath: 'polygon(57.4% 0, 100% 0, 100% 100%, 47.4% 100%)' }}
        initial={reduce ? false : { x: '60%' }}
        animate={{ x: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 30 }}
      />
      <div
        aria-hidden
        className="speed-lines pointer-events-none absolute inset-0 opacity-60 mix-blend-multiply"
      />

      {round && (
        <motion.div
          className="comic absolute top-4 left-1/2 z-20 -translate-x-1/2 border-3 border-ink bg-white px-4 pt-1.5 pb-1 text-[22px] shadow-ink-sm"
          initial={reduce ? false : { y: -80 }}
          animate={{ y: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 22, delay: 0.1 }}
        >
          TUR {round.current}/{round.total}
        </motion.div>
      )}

      {/* Phones: the VS burst gets its own middle column next to the avatars, so it never covers
          the names; from `sm` up it floats over the centre split. */}
      <div className="relative grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-1 px-3 pt-14 pb-24 [--vs-burst:88px] sm:grid-cols-2 sm:items-center sm:gap-x-0 sm:px-6 sm:[--vs-burst:clamp(150px,15vw,210px)]">
        <FighterSide f={left} side="left" reduce={reduce} />
        <VsBurst reduce={reduce} />
        <FighterSide f={right} side="right" reduce={reduce} />
      </div>

      {category && (
        <motion.div
          // Phones: pinned to both edges (minus a gutter) so a long category wraps instead of
          // running off the panel.
          className="absolute right-3 bottom-5 left-3 z-20 flex justify-center sm:right-auto sm:left-1/2 sm:block sm:-translate-x-1/2"
          initial={reduce ? { opacity: 0 } : { y: 120, rotate: 6 }}
          animate={{ y: 0, rotate: -2, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 360, damping: 20, delay: 0.75 }}
        >
          <div className="comic flex items-center gap-2 border-4 border-ink bg-yellow px-4 pt-2 pb-1.5 text-[22px] shadow-ink sm:gap-3 sm:px-6 sm:text-[clamp(26px,2.6vw,38px)] sm:whitespace-nowrap">
            <Emoji emoji={category.emoji} label="" size="1.1em" className="-mt-1" />
            {upper(category.name)}
          </div>
        </motion.div>
      )}
    </div>
  );
}
